import {
  ArrayInput,
  AutocompleteArrayInput,
  BooleanInput,
  CreateButton,
  EditButton,
  FormDataConsumer,
  Labeled,
  NumberInput,
  ReferenceArrayInput,
  regex,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useNotify,
  useRefresh,
  useSaveContext,
  useTranslate,
  useRecordContext,
} from 'react-admin';
import React from 'react';
import { Datagrid, IfCanAccess, List } from '@react-admin/ra-rbac';
import { Customer } from '@x-guard/xgac-types/xgac';
import { useRedirect } from 'ra-core';
import {
  Box, Button, Paper, Stack, Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { locales } from '../../../lib/constants/localesOptions';
import { EditWithSuccess } from '../../../components/baseForms/EditWithSuccess';
import ResponsePartnerBooleanField from '../../../components/fields/ResponsePartnerBooleanField';
import LengthField from '../../../components/fields/LengthField';
import { CreateWithSuccess } from '../../../components/baseForms/CreateWithSuccess';
import SearchFields from '../components/fields/Filters';
import { LIST_DEBOUNCE } from '../../../config';
import { FullExportButton } from '../../../utils/customExporter';
import { AlarmCenterField } from '../../../components/fields/AlarmCenterField';
import authProvider from '../../../utils/authProvider';
import { StabilityAlert, StabilityLevel } from '../../../components/StabilityAlert';
import { HasRoles } from '../../../components/HasRoles';
import { TimeGranularityInput } from '../../../components/inputs/timeGranularityInput';
import { LoraConfigInput } from '../../../components/inputs/LoraConfigInput';
import { loraDeviceTypes } from '../../../lib/constants/selectChoices';
import CustomBulkActionButtons from '../../../components/buttons/CustomBulkActionButtons';
import { CustomToolbar } from '../../../components/CustomToolBar';
import { DateFieldWithTime } from '../../../components/fields/DateFieldWithTime';
import { AuditlogButton } from '../components/buttons/AuditlogButton';
import { PhoneNumberInput } from '../../../components/inputs/PhoneNumberInput';

const BHVK_NAME_PREFIX = '[BHV-Knop.nl] ';

const productChoices = [
  { id: 'xgac', name: 'resources.customers.fields.customerProduct.xgac' },
  { id: 'bhvk', name: 'resources.customers.fields.customerProduct.bhvk' },
];

const transform = (data: Record<string, any>) => {

  const isBhvk = data.customerProduct === 'bhvk';

  delete data.migrationFinalised;
  delete data.children;
  delete data.parents;
  delete data.responsePartner;
  delete data.customerProduct;

  if (isBhvk) {

    const rawName = (data.name || '').trim();
    const name = rawName.startsWith(BHVK_NAME_PREFIX) ? rawName : `${BHVK_NAME_PREFIX}${rawName}`;
    const bhvkMergeConfig = {
      enabled: false,
      onlyMergeWhenSame: {
        name: false,
        severity: true,
        type: true,
      },
      maxTimeBetweenAlarmsMs: 900000,
      retriggerAtsAfterMs: 300000,
    };

    return {
      name,
      locale: data.locale || 'nl',
      timeZone: 'Europe/Amsterdam',
      type: 'bhvk_paid',
      preferences: {
        mergeConfig: data.useDefaultMergeConfig === false
          ? data.preferences?.mergeConfig
          : bhvkMergeConfig,
        disableAutomaticGenerationOfButtonZones:
          data.preferences?.disableAutomaticGenerationOfButtonZones,
        alarmAgeLimit: data.hasAlarmAgeLimit ? data.preferences?.alarmAgeLimit : null,
        enableZoneEnterExitEvents: data.preferences?.enableZoneEnterExitEvents,
      },
      parent: null,
      responsePartners: [],
      billingContracts: [],
    };

  }

  if (data.preferences && data.useDefaultMergeConfig) {

    data.preferences.mergeConfig = null;

  }

  if (data.preferences && !data.hasAlarmAgeLimit) {

    data.preferences.alarmAgeLimit = null;

  }

  delete data.hasAlarmAgeLimit;
  delete data.useDefaultMergeConfig;
  delete data.mergeConfig;

  if (data.billingContracts) {

    data.billingContracts = data.billingContracts.map((contract: { name: string }) => contract.name);

  }

  if (data.responsePartners) {

    data.responsePartners = data.responsePartners.map((partner: any) => {

      if (partner._id) {

        return partner;

      }

      return { _id: partner, _ref: 'Customer' };

    });

  }
  if (!data.alarmCenter?.enabled && !data.alarmCenter?.queueName) {

    delete data.alarmCenter;

  } else if (data.alarmCenter?.enabled) {

    return {
      name: data.name,
      locale: data.locale,
      alarmCenter: data.alarmCenter,
      billingContracts: data.billingContracts,
      preferences: data.preferences,
    };

  }
  return {
    ...data,
  };

};

const PreviewSection = ({ title, items }: { title: string; items: string[] }) => (

  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{title}</Typography>
    <Box component="ul" sx={{ m: 0, pl: 2 }}>
      {items.map((item) => (
        <Typography component="li" variant="body2" key={item} sx={{ mb: 0.25 }}>{item}</Typography>
      ))}
    </Box>
  </Box>
);

const CustomerCreatePreview = ({ formData }: { formData: Record<string, any> }) => {

  const translate = useTranslate();
  const isBhvk = formData.customerProduct === 'bhvk';
  const name = (formData.name || '').trim() || '…';
  const locale = formData.locale || 'nl';

  if (isBhvk) {

    const displayName = name.startsWith(BHVK_NAME_PREFIX) ? name : `${BHVK_NAME_PREFIX}${name}`;
    return (
      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {translate('resources.customers.text.create_preview_title')}
        </Typography>
        <PreviewSection title={translate('resources.customers.text.create_preview.customer')} items={[
          displayName,
          `locale: ${locale}`,
          'timeZone: Europe/Amsterdam',
          'type: bhvk_paid',
          translate('resources.customers.text.create_preview.bhvk_merge'),
          translate('resources.customers.text.create_preview.bhvk_no_relations'),
        ]}/>
        <PreviewSection title={translate('resources.customers.text.create_preview.structure')} items={[
          'Asset groups: BHV-Knoppen, BHV, Paniekknoppen, AIT, Basistations',
          'Zone: Opvolging wereld',
        ]}/>
        <PreviewSection title={translate('resources.customers.text.create_preview.triggers')} items={[
          'BHV-Knop ingedrukt',
          'Paniekknop ingedrukt',
          'Alarm - Mandown - bewusteloos',
          'Alarm - Paniekalarm',
          'Alarm - Assistentievraag',
          'Alarm - Testmelding',
        ]}/>
        <PreviewSection title={translate('resources.customers.text.create_preview.ats')} items={[
          translate('resources.customers.text.create_preview.bhvk_ats_button'),
          translate('resources.customers.text.create_preview.bhvk_ats_paniek'),
          translate('resources.customers.text.create_preview.bhvk_ats_other'),
        ]}/>
        <PreviewSection title={translate('resources.customers.text.create_preview.qr')} items={[
          translate('resources.customers.text.create_preview.bhvk_qr'),
        ]}/>
        <PreviewSection title={translate('resources.customers.text.create_preview.reports')} items={[
          translate('resources.customers.text.create_preview.bhvk_report_alarms'),
          translate('resources.customers.text.create_preview.bhvk_report_inactivity'),
        ]}/>
      </Paper>
    );

  }

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {translate('resources.customers.text.create_preview_title')}
      </Typography>
      <PreviewSection title={translate('resources.customers.text.create_preview.customer')} items={[
        name,
        `locale: ${locale}`,
        'type: default',
      ]}/>
      <PreviewSection title={translate('resources.customers.text.create_preview.structure')} items={[
        'Zone: Opvolging wereld',
        'Contact list: Contacten',
      ]}/>
      <PreviewSection title={translate('resources.customers.text.create_preview.triggers')} items={[
        'Alarm - Paniekalarm',
        'Alarm - Assistentiealarm',
        'Alarm - Testmelding',
        'Alarm - Mandown – bewusteloos',
      ]}/>
      <PreviewSection title={translate('resources.customers.text.create_preview.reports')} items={[
        'Appgebruik - maandelijks',
        'Alarmen - wekelijks (red + orange)',
      ]}/>
      <PreviewSection title={translate('resources.customers.text.create_preview.ats')} items={[
        translate('resources.customers.text.create_preview.xgac_ats'),
      ]}/>
    </Paper>
  );

};

export const CustomerList = () => {

  return (
    <>
      <StabilityAlert stability={StabilityLevel.Stable}/>
      <List filters={SearchFields} debounce={LIST_DEBOUNCE} title="resources.customers.text.title"
        actions={<TopToolbar><CreateButton/><FullExportButton resource="customers"/></TopToolbar>}>
        <Datagrid rowClick="toggleSelection" bulkActionButtons={<CustomBulkActionButtons/>}>
          <TextField source="name" label="general.fields.name"/>
          <TextField source="locale" />
          <ResponsePartnerBooleanField source="responsePartner" label="Is response partner"/>
          <LengthField source="responsePartners"/>
          <AlarmCenterField />
          <DateFieldWithTime source="createdAt" label="general.fields.createdAt" timeOnHover={true}/>
          <DateFieldWithTime source="updatedAt" label="general.fields.updatedAt" timeOnHover={true}/>
          <IfCanAccess action="edit">
            <EditButton/>
          </IfCanAccess>
          <HasRoles anyOf={['developer_admin']}>
            <AuditlogButton/>
          </HasRoles>
        </Datagrid>
      </List>
    </>
  );

};

const DisabledMergeConfig = () => {

  return (
    <>
      <BooleanInput source="preferences.mergeConfig.enabled" label="resources.customers.fields.mergeConfig.enabled" disabled defaultValue={true}/>
      <Labeled label="resources.customers.fields.mergeConfig.onlyMergeWhenSame">
        <>
          <div className="flex-in-between">
            <BooleanInput source="preferences.mergeConfig.onlyMergeWhenSame.name" label="resources.alarms.fields.name" disabled defaultValue={false}/>
            <BooleanInput source="preferences.mergeConfig.onlyMergeWhenSame.severity" label="resources.alarms.fields.severity" disabled defaultValue={true}/>
            <BooleanInput source="preferences.mergeConfig.onlyMergeWhenSame.type" label="resources.alarms.fields.type" disabled defaultValue={true}/>
          </div>
        </>
      </Labeled>
      <NumberInput source="preferences.mergeConfig.maxTimeBetweenAlarmsMs"
        disabled
        defaultValue={900000} format={(value) => value / 1000}
        label="resources.customers.fields.mergeConfig.maxTimeBetweenAlarmsMs"
      />
      <NumberInput source="preferences.mergeConfig.retriggerAtsAfterMs"
        disabled
        defaultValue={300000} format={(value) => value / 1000}
        label="resources.customers.fields.mergeConfig.retriggerAtsAfterMs"
      />
    </>
  );

};

const BhvkDisabledMergeConfig = () => (

  <>
    <BooleanInput source="preferences.mergeConfig.enabled" label="resources.customers.fields.mergeConfig.enabled" disabled defaultValue={false}/>
    <Labeled label="resources.customers.fields.mergeConfig.onlyMergeWhenSame">
      <>
        <div className="flex-in-between">
          <BooleanInput source="preferences.mergeConfig.onlyMergeWhenSame.name" label="resources.alarms.fields.name" disabled defaultValue={false}/>
          <BooleanInput source="preferences.mergeConfig.onlyMergeWhenSame.severity" label="resources.alarms.fields.severity" disabled defaultValue={true}/>
          <BooleanInput source="preferences.mergeConfig.onlyMergeWhenSame.type" label="resources.alarms.fields.type" disabled defaultValue={true}/>
        </div>
      </>
    </Labeled>
    <NumberInput source="preferences.mergeConfig.maxTimeBetweenAlarmsMs"
      disabled
      defaultValue={900000} format={(value) => value / 1000}
      label="resources.customers.fields.mergeConfig.maxTimeBetweenAlarmsMs"
    />
    <NumberInput source="preferences.mergeConfig.retriggerAtsAfterMs"
      disabled
      defaultValue={300000} format={(value) => value / 1000}
      label="resources.customers.fields.mergeConfig.retriggerAtsAfterMs"
    />
  </>
);

const AlarmMergeConfig = () => (
  <>
    <BooleanInput source="preferences.mergeConfig.enabled" label="resources.customers.fields.mergeConfig.enabled" defaultValue={true}/>
    <Labeled label="resources.customers.fields.mergeConfig.onlyMergeWhenSame">
      <>
        <div className="flex-in-between">
          <BooleanInput source="preferences.mergeConfig.onlyMergeWhenSame.name" defaultValue={false} label="resources.alarms.fields.name"/>
          <BooleanInput source="preferences.mergeConfig.onlyMergeWhenSame.severity" defaultValue={true} label="resources.alarms.fields.severity"/>
          <BooleanInput source="preferences.mergeConfig.onlyMergeWhenSame.type" defaultValue={true} label="resources.alarms.fields.type"/>
        </div>
      </>
    </Labeled>
    <NumberInput source="preferences.mergeConfig.maxTimeBetweenAlarmsMs"
      defaultValue={900000} format={(value) => value / 1000}
      parse={(value) => value * 1000}
      label="resources.customers.fields.mergeConfig.maxTimeBetweenAlarmsMs"
    />
    <NumberInput source="preferences.mergeConfig.retriggerAtsAfterMs"
      defaultValue={300000} format={(value) => value / 1000}
      parse={(value) => value * 1000}
      label="resources.customers.fields.mergeConfig.retriggerAtsAfterMs"
    />
  </>
);

const CustomSaveButton = () => {

  const { saving } = useSaveContext();
  return (
    <Toolbar>
      <SaveButton disabled={saving} />
    </Toolbar>
  );

};

const CustomerClientConfigButton = () => {

  const record = useRecordContext<any>();
  const translate = useTranslate();
  if (!record?._id) {

    return null;

  }
  return (
    <Button
      component={Link}
      to={`/client-config-manager?customer=${record._id}`}
      variant="outlined"
      sx={{ mb: 1 }}
    >
      {translate('clientConfig.buttons.openManager')}
    </Button>
  );

};
export const CustomerCreate = () => {

  const isAdmin = authProvider.isAdmin();
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();

  const onSuccess = (data: Customer & {
    id: string;
    type?: string;
  }) => {

    notify('resources.customers.text.create_success');
    const newCustomer = {
      label: data.name,
      value: data.id,
    };
    localStorage.setItem('lastSavedCustomer', JSON.stringify(newCustomer));
    sessionStorage.setItem('currentCustomer', JSON.stringify(newCustomer));
    try {

      const list = JSON.parse(localStorage.getItem('customers') || '[]') as { value: string; label: string }[];
      if (newCustomer.value && !list.some((c) => c.value === newCustomer.value)) {

        localStorage.setItem('customers', JSON.stringify([newCustomer, ...list]));

      }

    } catch {

      // ignore malformed customers cache; reload will rebuild it

    }
    redirect('list', 'customer-alarm-center-relations');
    window.location.reload();

  };

  return (
    <>
      <StabilityAlert stability={StabilityLevel.Unstable} />
      <CreateWithSuccess transform={transform} mutationOptions={{ onSuccess }} actions={false} title="resources.customers.text.title" >
        <SimpleForm toolbar={<CustomSaveButton/>}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            alignItems="flex-start"
            sx={{ width: '100%' }}
          >
            <Box sx={{
              display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, width: '100%',
            }}>
              {isAdmin && (
                <SelectInput
                  source="customerProduct"
                  choices={productChoices}
                  defaultValue="xgac"
                  label="resources.customers.fields.customerProduct.label"
                  validate={required()}
                  fullWidth
                />
              )}
              <TextInput source="name" label="general.fields.name" fullWidth/>
              <SelectInput source="locale" choices={locales} defaultValue='nl' validate={required()} fullWidth />
              <FormDataConsumer>
                {({ formData }) => {

                  const isBhvk = formData.customerProduct === 'bhvk';

                  return (
                    <>
                      {isAdmin && !isBhvk && (
                        <>
                          <BooleanInput
                            source="alarmCenter.enabled"
                            defaultValue={false}
                            label={`Is ${translate('resources.customers.fields.alarmCenter_name')}`}
                          />
                          <HasRoles anyOf={['developer_admin']}>
                            <ArrayInput source="billingContracts">
                              <SimpleFormIterator>
                                <TextInput
                                  source="name"
                                  label=""
                                  validate={regex(
                                    /[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89aAbB][a-f0-9]{3}-[a-f0-9]{12}/,
                                    'resources.customers.text.uuid_validation',
                                  )}
                                  fullWidth
                                />
                              </SimpleFormIterator>
                            </ArrayInput>
                          </HasRoles>
                          {formData.alarmCenter?.enabled && (
                            <>
                              <TextInput
                                source="alarmCenter.queueName"
                                label='resources.customers.text.alarmcenter_queueName'
                                fullWidth
                              />
                              <PhoneNumberInput
                                source={'alarmCenter.callbackPhoneNumber'}
                                label={'resources.customers.fields.alarmCenter.phoneNumber'}
                                size="medium"
                              />
                            </>
                          )}
                          {(!formData.alarmCenter || !formData.alarmCenter.enabled) && (
                            <ReferenceArrayInput source="responsePartners" reference="responsePartners">
                              <AutocompleteArrayInput
                                label="resources.customers.fields.responsePartner_name"
                                source="name"
                                autoHighlight={true}
                                blurOnSelect={false}
                                fullWidth
                              />
                            </ReferenceArrayInput>
                          )}
                        </>
                      )}
                      {(isAdmin || isBhvk) && (
                        <BooleanInput
                          source="preferences.disableAutomaticGenerationOfButtonZones"
                          label={'resources.customers.fields.disableAutomaticGenerationOfButtonZones'}
                        />
                      )}
                      <HasRoles anyOf={['developer_admin']}>
                        <BooleanInput
                          source="useDefaultMergeConfig"
                          label="resources.customers.fields.mergeConfig.use_default"
                          defaultValue={true}
                        />
                        {formData.useDefaultMergeConfig && isBhvk && <BhvkDisabledMergeConfig />}
                        {formData.useDefaultMergeConfig && !isBhvk && <DisabledMergeConfig />}
                        {!formData.useDefaultMergeConfig && <AlarmMergeConfig/>}
                        <BooleanInput source="hasAlarmAgeLimit"/>
                        {formData.hasAlarmAgeLimit && (
                          <TimeGranularityInput
                            source="preferences.alarmAgeLimit"
                            label="resources.customers.fields.alarmAgeLimit"
                          />
                        )}
                        <BooleanInput
                          source="preferences.enableZoneEnterExitEvents"
                          defaultValue={false}
                          label="resources.customers.fields.enableZoneEnterExitEvents"
                        />
                      </HasRoles>
                    </>
                  );

                }}
              </FormDataConsumer>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <FormDataConsumer>
                {({ formData }) => <CustomerCreatePreview formData={formData} />}
              </FormDataConsumer>
            </Box>
          </Stack>
        </SimpleForm>
      </CreateWithSuccess>
    </>
  );

};
export const CustomerEdit = () => {

  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const OnSuccess = () => {

    notify('resources.customers.text.update_success');
    refresh();

  };

  const isAdmin = authProvider.isAdmin();
  return (
    <>
      <StabilityAlert stability={StabilityLevel.Unstable} />
      <EditWithSuccess transform={transform} mutationOptions={{ onSuccess: OnSuccess }} title="resources.customers.text.title">
        <SimpleForm toolbar={<CustomToolbar/>}>
          <TextInput source="name" label="general.fields.name" />
          <SelectInput source="locale" choices={locales} defaultValue='nl' validate={required()} />
          <HasRoles anyOf={['developer_admin']}>
            <CustomerClientConfigButton />
          </HasRoles>
          {isAdmin && (
            <>
              <BooleanInput source="alarmCenter.enabled" defaultValue={false} label={`Is ${translate('resources.customers.fields.alarmCenter_name')}`}/>
              <HasRoles anyOf={['developer_admin']}>
                <ArrayInput source="billingContracts">
                  <SimpleFormIterator>
                    <TextInput source="name" label=""
                      validate={regex(/[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89aAbB][a-f0-9]{3}-[a-f0-9]{12}/, 'resources.customers.text.uuid_validation')}
                      fullWidth
                    />
                  </SimpleFormIterator>
                </ArrayInput>
              </HasRoles>
              <FormDataConsumer>
                {({ formData }) => (formData.alarmCenter?.enabled)
                  && <><TextInput source="alarmCenter.queueName" label='resources.customers.text.alarmcenter_queueName' />
                    <PhoneNumberInput source={'alarmCenter.callbackPhoneNumber'} label={'resources.customers.fields.alarmCenter.phoneNumber'} size="medium"/>
                  </>
                }
              </FormDataConsumer>
              <FormDataConsumer>
                {({ formData }) => (!formData.alarmCenter || !formData.alarmCenter.enabled)
                  && <>
                    <ReferenceArrayInput source="responsePartners" reference="responsePartners">
                      <AutocompleteArrayInput label="resources.customers.fields.responsePartner_name" source="name" autoHighlight={true} blurOnSelect={false}/>
                    </ReferenceArrayInput>
                  </>
                }
              </FormDataConsumer>
              <BooleanInput source="useDefaultMergeConfig" defaultValue={true} label="resources.customers.fields.mergeConfig.use_default"/>
              <FormDataConsumer>
                {({ formData }) => {

                  if (formData.useDefaultMergeConfig) {

                    return <DisabledMergeConfig />;

                  }
                  return <AlarmMergeConfig/>;

                }}
              </FormDataConsumer>
              <BooleanInput
                source="preferences.disableAutomaticGenerationOfButtonZones"
                label={'resources.customers.fields.disableAutomaticGenerationOfButtonZones'}
              />
              <BooleanInput
                source="preferences.recurringTasksEnabled"
                defaultValue={false}
                label="resources.customers.fields.recurringTasksEnabled"
              />
              <BooleanInput
                source="preferences.securityInventoryEnabled"
                defaultValue={false}
                label="resources.customers.fields.securityInventoryEnabled"
              />
              <HasRoles anyOf={['developer_admin']}>
                <BooleanInput source="hasAlarmAgeLimit"/>
                <FormDataConsumer>
                  {({ formData }) => (formData.hasAlarmAgeLimit

                    && <TimeGranularityInput source="preferences.alarmAgeLimit" label="resources.customers.fields.alarmAgeLimit"/>
                  )}
                </FormDataConsumer>
              </HasRoles>
              <BooleanInput source={'userPolicies.disablePasswordlessLogin'}/>
              <HasRoles anyOf={['developer_admin']}>
                <BooleanInput
                  source="preferences.enableZoneEnterExitEvents"
                  defaultValue={false}
                  label="resources.customers.fields.enableZoneEnterExitEvents"
                />
              </HasRoles>
              <ArrayInput source="loraConfig">
                <SimpleFormIterator>
                  <SelectInput source="type" defaultValue={null} choices={loraDeviceTypes}
                    emptyText="resources.devices.text.no_lora_config"
                    label="resources.devices.fields.loraConfig.type"
                    validate={required()}/>
                  <LoraConfigInput/>
                </SimpleFormIterator>
              </ArrayInput>
            </>
          )}
        </SimpleForm>
      </EditWithSuccess>
    </>
  );

};
